import { TaxEstimateType } from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import { WSAlert, WSElement } from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useQueryCustomerEntity } from "../../../query/onboarding/queries/useQueryCustomerEntity";
import { useTaxEstimate } from "../../../query/taxes/queries";
import {
  useActivities,
  useMemberProfile,
  useUserProfile
} from "../../../query/users/queries";
import { useOnVisible } from "../../../shared/hooks/useOnVisible";
import { useWSAnalytics } from "../../../utils/WSAnalytics";
import { PATH_ONBOARDING_TAX_WITHHOLDINGS } from "../../Onboarding";
import { useModalDismissTaxWithholdingsSetup } from "../modals/ModalDismissTaxWithholdingsSetup";
import { useModalPauseReminders } from "../modals/ModalPauseReminders";
import { selectorProposeTaxWithholdings } from "../selectors/selectorProposeTaxWithholdings";

type Props = {
  invoiceNetAmount?: number;
  invoiceCurrency?: Currency;
};

export const AlertEstimatedTaxWithholdings: React.FC<Props> = props => {
  const history = useHistory();
  const userId = useUserId();
  const queryUser = useUserProfile(userId);
  const queryMember = useMemberProfile(userId);
  const queryActivity = useActivities(userId);
  const queryMonitoredEntity = useQueryCustomerEntity();
  const queryFeatureFlags = useFeatureFlags();
  const queryInternalAccounts = useQueryInternalAccounts();

  const modalDismissTaxWithholdingsSetup =
    useModalDismissTaxWithholdingsSetup();
  const modalPauseReminders = useModalPauseReminders();

  const handleDismiss = () => {
    modalDismissTaxWithholdingsSetup.open({
      onPauseReminders: () => {
        modalPauseReminders.open();
      }
    });
  };

  const analytics = useWSAnalytics();

  const showTaxWithholdingAlert = useMemo(() => {
    if (
      !queryFeatureFlags.data ||
      !queryFeatureFlags.data.taxWithholdingInvoicingAlerts
    ) {
      return false;
    }

    if (
      !queryActivity.data ||
      queryActivity.data.events.taxWithholdingsAlertsDismissed
    ) {
      return false;
    }

    // If user didn't set income estimate, it means they didn't go through estimated tax rate flow, so we can't calculate it
    if (!queryMember.data?.profile.taxInfo?.incomeEstimateForYear) {
      return false;
    }

    return selectorProposeTaxWithholdings(
      queryUser.data,
      queryMember.data,
      queryMonitoredEntity.data,
      queryInternalAccounts.data
    );
  }, [
    queryFeatureFlags.data,
    queryActivity.data,
    queryMember.data,
    queryUser.data,
    queryMonitoredEntity.data,
    queryInternalAccounts.data
  ]);

  const queryTaxEstimate = useTaxEstimate(
    {
      type: TaxEstimateType.Base,
      year: new Date().getFullYear()
    },
    {
      enabled: showTaxWithholdingAlert
    }
  );

  const [ref] = useOnVisible(() => {
    analytics.track.other("Invoicing Tax Withholdings Alert Viewed", {
      variant: "estimated_tax_rate"
    });
  });

  if (!showTaxWithholdingAlert) {
    return null;
  }

  const esitimatedTaxRate = queryTaxEstimate.data?.taxRates.effective || 0;

  if (esitimatedTaxRate === 0) {
    return null;
  }

  const recommendedInvoiceAmount = props.invoiceNetAmount
    ? (props.invoiceNetAmount * esitimatedTaxRate) / 100
    : undefined;

  return (
    <WSAlert
      theme="warning"
      icon="alert"
      title="Stay on track for taxes"
      action={{
        text: "Complete your setup",
        onClick: () => {
          analytics.track.other("Invoicing Tax Withholdings Alert Click", {
            variant: "estimated_tax_rate"
          });
          history.push(PATH_ONBOARDING_TAX_WITHHOLDINGS);
        }
      }}
      onDismiss={handleDismiss}
    >
      <WSElement ref={ref}></WSElement>
      {recommendedInvoiceAmount && props.invoiceCurrency ? (
        <>
          Based on your tax profile, we recommend setting aside{" "}
          {formatMoney(recommendedInvoiceAmount, props.invoiceCurrency)} from
          this invoice to cover the taxes you'll owe as an independent
          contractor. Taking action now helps you stay on track—no scrambling
          later.
        </>
      ) : (
        <>
          Based on your tax profile, we recommend setting aside{" "}
          {esitimatedTaxRate}% from your earnings to cover the taxes you’ll owe
          as an independent contractor. Taking action now helps you stay on
          track—no scrambling later.
        </>
      )}
    </WSAlert>
  );
};
